// Phase 0094-P1 — Salary Register report.
//
// Wide dynamic-head salary register: one column per head used in the run.

import { useState } from 'react'
import {
  App, Button, Card, Empty, Input, Skeleton, Space, Table, Tag, Typography,
} from 'antd'
import { useQuery } from '@tanstack/react-query'

import { payrollApi, type SalaryRegister } from '@/features/payroll/payrollApi'

const { Text, Title } = Typography

export default function SalaryRegisterPage() {
  const { message } = App.useApp()
  const [runId, setRunId] = useState<string>('')
  const [submitted, setSubmitted] = useState<string | null>(null)

  const { data, isLoading } = useQuery<SalaryRegister>({
    queryKey: ['salary-register', submitted],
    queryFn: () => payrollApi.salaryRegister(submitted!),
    enabled: !!submitted,
  })

  const earningHeads = (data?.heads ?? []).filter(h => h.head_type === 'EARNING')
  const deductionHeads = (data?.heads ?? []).filter(h => h.head_type === 'DEDUCTION')

  const columns: unknown[] = [
    { title: 'Code', dataIndex: 'employee_code', key: 'code',
      render: (c: string | null) => c ?? '—', fixed: 'left', width: 100 },
    { title: 'Name', dataIndex: 'employee_name', key: 'name',
      fixed: 'left', width: 180 },
    { title: 'Dept', dataIndex: 'department', key: 'dept', width: 140 },
    { title: 'Type', dataIndex: 'salary_type', key: 'type',
      render: (t: string | null) => t ? <Tag>{t}</Tag> : '—', width: 110 },
    { title: 'Days', dataIndex: 'payable_days', key: 'days', width: 70 },
    { title: 'OT', dataIndex: 'ot_hours', key: 'ot', width: 60 },
    ...earningHeads.map(h => ({
      title: h.head_code, key: `e_${h.head_code}`,
      render: (_: unknown, r: SalaryRegister['rows'][number]) => (
        r.heads[h.head_code] ? `₹${r.heads[h.head_code].toLocaleString()}` : '—'
      ),
      width: 100,
    })),
    { title: 'Gross', dataIndex: 'gross', key: 'gross', width: 110,
      render: (n: number) => <Text strong>₹{n.toLocaleString()}</Text> },
    ...deductionHeads.map(h => ({
      title: h.head_code, key: `d_${h.head_code}`,
      render: (_: unknown, r: SalaryRegister['rows'][number]) => (
        r.heads[h.head_code] ? `₹${r.heads[h.head_code].toLocaleString()}` : '—'
      ),
      width: 110,
    })),
    { title: 'Deductions', dataIndex: 'total_deductions', key: 'totd', width: 110,
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'Net Payable', dataIndex: 'net_payable', key: 'net', width: 130,
      render: (n: number) => <Text strong style={{ color: '#1677ff' }}>₹{n.toLocaleString()}</Text> },
    { title: 'A/c', dataIndex: 'bank_account_masked', key: 'acc', width: 140,
      render: (a: string | null) => a ?? '—' },
    { title: 'Status', dataIndex: 'calculation_status', key: 'status',
      render: (s: string) => <Tag>{s}</Tag>, width: 130 },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Salary Register</Title>
      <Text type="secondary">
        Wide register view: every earning + deduction head used in the run, plus net payable.
      </Text>

      <Card style={{ marginTop: 16 }}>
        <Space>
          <Input
            placeholder="Paste Run ID"
            style={{ width: 360 }}
            value={runId}
            onChange={(e) => setRunId(e.target.value)}
          />
          <Button type="primary"
                  onClick={() => {
                    if (!runId.trim()) { message.error('Run ID required'); return }
                    setSubmitted(runId.trim())
                  }}>
            Load
          </Button>
        </Space>
      </Card>

      {isLoading && <Card style={{ marginTop: 16 }}><Skeleton active /></Card>}

      {data && (
        <Card style={{ marginTop: 16 }}
              title={`Run ${data.run_id.slice(0,8)} — ${data.run_status}`}
              extra={
                <Space>
                  <Text>Employees: <strong>{data.totals.employees}</strong></Text>
                  <Text>Gross: <strong>₹{data.totals.gross.toLocaleString()}</strong></Text>
                  <Text>Net: <strong style={{ color: '#1677ff' }}>
                    ₹{data.totals.net.toLocaleString()}</strong></Text>
                </Space>
              }>
          {data.rows.length === 0
            ? <Empty description="No employee rows in this run." />
            : (
              <Table
                rowKey="employee_id"
                columns={columns as never}
                dataSource={data.rows}
                pagination={{ pageSize: 50 }}
                size="small"
                scroll={{ x: 1800 }}
              />
            )}
        </Card>
      )}
    </div>
  )
}
